import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, Check, Edit3 } from 'lucide-react';
import { useUserTimezone } from '../../hooks/useUserTimezone';

/**
 * QuickEntryTile - Manual health metric entry for clients
 *
 * Dropdown-based single-metric entry flow:
 * Select metric → see current value → enter new → save
 *
 * Fields: Weight, Steps, Sleep, Body Fat %, Lean Muscle Mass, Water
 * Confidence: 0.60 (manual entry — device data at 0.90 wins)
 */

interface MetricConfig {
  key: string;
  label: string;
  apiField: string;
  unit: string;
  step: number;
  min: number;
  max: number;
  placeholder: string;
}

const METRICS: MetricConfig[] = [
  { key: 'weight', label: 'Weight', apiField: 'weight', unit: 'lbs', step: 0.1, min: 50, max: 800, placeholder: 'e.g. 185.0' },
  { key: 'steps', label: 'Steps', apiField: 'steps', unit: 'steps', step: 1, min: 0, max: 100000, placeholder: 'e.g. 8000' },
  { key: 'sleep', label: 'Sleep', apiField: 'sleep_hours', unit: 'hours', step: 0.1, min: 0, max: 24, placeholder: 'e.g. 7.5' },
  { key: 'body_fat', label: 'Body Fat', apiField: 'body_fat_percent', unit: '%', step: 0.1, min: 1, max: 70, placeholder: 'e.g. 25.0' },
  { key: 'lean_mass', label: 'Lean Mass', apiField: 'lean_muscle_mass', unit: 'lbs', step: 0.1, min: 20, max: 400, placeholder: 'e.g. 155.0' },
  { key: 'water', label: 'Water', apiField: 'water_intake', unit: 'oz', step: 1, min: 0, max: 300, placeholder: 'e.g. 80' },
];

const QuickEntryTile: React.FC = () => {
  const { getToday } = useUserTimezone();
  const [selectedMetric, setSelectedMetric] = useState<string>('weight');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => getToday());
  const [currentValues, setCurrentValues] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [dropdownOpen]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Fetch current day's record to show existing values
  const fetchCurrentValues = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/health/daily-record/${selectedDate}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const values: Record<string, number | null> = {};
        if (data.record?.actual_values) {
          const av = data.record.actual_values;
          // Map API field names to our metric keys
          for (const metric of METRICS) {
            const val = av[metric.apiField];
            values[metric.key] = val != null ? val : null;
          }
        }
        setCurrentValues(values);
      } else {
        setCurrentValues({});
      }
    } catch {
      setCurrentValues({});
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchCurrentValues();
  }, [fetchCurrentValues]);

  const activeMetric = METRICS.find(m => m.key === selectedMetric);
  const currentVal = selectedMetric ? currentValues[selectedMetric] : null;

  const handleSave = async () => {
    if (!activeMetric || inputValue === '') return;
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) return;

    setSaving(true);
    try {
      const response = await fetch('/api/health/metrics/quick-entry', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date: selectedDate,
          values: { [activeMetric.apiField]: numValue },
          source: 'manual_entry',
          confidence: 0.60
        })
      });

      if (response.ok) {
        setSaveSuccess(true);
        setInputValue('');
        // Refresh current values to show the update
        await fetchCurrentValues();
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (err) {
      console.error('[QuickEntryTile] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  // Max date = today (no future entries)
  const today = getToday();

  return (
    <div className="card quick-entry-tile">
      <div className="qe-header">
        <h3><Edit3 size={16} /> Quick Entry</h3>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          max={today}
          className="qe-date-picker"
        />
      </div>

      {/* Metric Dropdown */}
      <div className="qe-dropdown-wrapper" ref={dropdownRef}>
        <button
          className="qe-dropdown-trigger"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <span>{activeMetric ? activeMetric.label : 'Select metric...'}</span>
          <ChevronDown size={14} className={`qe-chevron ${dropdownOpen ? 'open' : ''}`} />
        </button>
        {dropdownOpen && (
          <div className="qe-dropdown-menu">
            {METRICS.map(metric => (
              <button
                key={metric.key}
                className={`qe-dropdown-item ${selectedMetric === metric.key ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedMetric(metric.key);
                  setDropdownOpen(false);
                  setInputValue('');
                  setSaveSuccess(false);
                }}
              >
                <span className="qe-item-label">{metric.label}</span>
                {currentValues[metric.key] != null && (
                  <span className="qe-item-current">
                    {currentValues[metric.key]} {metric.unit}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Entry Area — shown when metric selected */}
      {activeMetric && (
        <div className="qe-entry-area">
          {/* Current value display */}
          <div className="qe-current">
            {loading ? (
              <span className="qe-current-label">Loading...</span>
            ) : currentVal != null ? (
              <>
                <span className="qe-current-label">Current:</span>
                <span className="qe-current-value">{currentVal} {activeMetric.unit}</span>
              </>
            ) : (
              <span className="qe-current-label">No data for this date</span>
            )}
          </div>

          {/* Input + Save */}
          <div className="qe-input-row">
            <div className="qe-input-wrapper">
              <input
                type="number"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={activeMetric.placeholder}
                step={activeMetric.step}
                min={activeMetric.min}
                max={activeMetric.max}
                className="qe-input"
                autoFocus
              />
              <span className="qe-unit">{activeMetric.unit}</span>
            </div>
            <button
              className={`qe-save-btn ${saveSuccess ? 'success' : ''}`}
              onClick={handleSave}
              disabled={saving || inputValue === ''}
            >
              {saving ? '...' : saveSuccess ? <Check size={14} /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .quick-entry-tile {
          padding: 16px;
          min-height: 180px;
          overflow: visible !important;
          position: relative;
        }

        .qe-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .qe-header h3 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .qe-header h3 svg {
          color: var(--accent-green);
        }

        .qe-date-picker {
          padding: 4px 8px;
          border: 1px solid var(--border-secondary);
          border-radius: 6px;
          font-size: 12px;
          background: var(--bg-primary);
          color: var(--text-primary);
          cursor: pointer;
        }

        /* Dropdown */
        .qe-dropdown-wrapper {
          position: relative;
          margin-bottom: 12px;
          z-index: 20;
        }

        .qe-dropdown-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: 8px;
          font-size: 13px;
          color: var(--text-primary);
          cursor: pointer;
          transition: border-color 0.15s ease;
        }

        .qe-dropdown-trigger:hover {
          border-color: var(--accent-green);
        }

        .qe-chevron {
          transition: transform 0.15s ease;
          color: var(--text-secondary);
        }

        .qe-chevron.open {
          transform: rotate(180deg);
        }

        .qe-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: 8px;
          overflow: hidden;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .qe-dropdown-item {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: none;
          border: none;
          font-size: 13px;
          color: var(--text-primary);
          cursor: pointer;
          transition: background 0.1s ease;
        }

        .qe-dropdown-item:hover {
          background: var(--bg-primary);
        }

        .qe-dropdown-item.selected {
          background: rgba(16, 185, 129, 0.1);
        }

        .qe-item-current {
          font-size: 12px;
          color: var(--text-secondary);
        }

        /* Entry Area */
        .qe-entry-area {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .qe-current {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: var(--bg-primary);
          border-radius: 6px;
          font-size: 12px;
        }

        .qe-current-label {
          color: var(--text-secondary);
        }

        .qe-current-value {
          color: var(--text-primary);
          font-weight: 600;
        }

        .qe-input-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .qe-input-wrapper {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: 8px;
          transition: border-color 0.15s ease;
        }

        .qe-input-wrapper:focus-within {
          border-color: var(--accent-green);
        }

        .qe-input {
          flex: 1;
          padding: 10px 0;
          background: none;
          border: none;
          font-size: 14px;
          color: var(--text-primary);
          outline: none;
          min-width: 0;
        }

        .qe-input::placeholder {
          color: var(--text-tertiary);
          font-size: 12px;
        }

        /* Hide number input spinners */
        .qe-input::-webkit-outer-spin-button,
        .qe-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .qe-input[type=number] {
          -moz-appearance: textfield;
        }

        .qe-unit {
          font-size: 12px;
          color: var(--text-secondary);
          white-space: nowrap;
        }

        .qe-save-btn {
          padding: 10px 20px;
          background: var(--accent-green);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
          min-width: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .qe-save-btn:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .qe-save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .qe-save-btn.success {
          background: #059669;
        }
      `}</style>
    </div>
  );
};

export default QuickEntryTile;
